import { Fragment } from 'react';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';

/** Split a line on **bold** spans. Deliberately tiny — bold, bullets and numbered lists only. */
function renderInline(line: string) {
  const parts = line.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, index) =>
    index % 2 === 1 ? <strong key={index}>{part}</strong> : <Fragment key={index}>{part}</Fragment>,
  );
}

const BULLET_PATTERN = /^\s*(?:[-*•]|\d+[.)])\s+(.*)$/;

type Block = { kind: 'paragraph'; text: string } | { kind: 'list'; items: { marker: string; text: string }[] };

/**
 * Group lines into paragraphs and lists so replies keep their structure:
 * blank lines become block spacing, consecutive bullet/numbered lines become
 * one list, and wrapped list lines hang-indent past their marker.
 */
function parseBlocks(content: string): Block[] {
  const blocks: Block[] = [];
  let paragraph: string[] = [];

  const flushParagraph = () => {
    if (paragraph.length) {
      blocks.push({ kind: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }
  };

  for (const line of content.split('\n')) {
    const trimmed = line.trim();
    if (!trimmed) {
      flushParagraph();
      continue;
    }
    const bullet = BULLET_PATTERN.exec(trimmed);
    if (bullet) {
      flushParagraph();
      const marker = /^\d/.test(trimmed) ? trimmed.slice(0, trimmed.indexOf(' ')).replace(')', '.') : '•';
      const last = blocks[blocks.length - 1];
      const item = { marker, text: bullet[1] };
      if (last?.kind === 'list') {
        last.items.push(item);
      } else {
        blocks.push({ kind: 'list', items: [item] });
      }
      continue;
    }
    // A "Heading:"-style line is its own block even without blank lines around it.
    if (trimmed.endsWith(':')) {
      flushParagraph();
      blocks.push({ kind: 'paragraph', text: trimmed });
      continue;
    }
    paragraph.push(trimmed);
  }
  flushParagraph();

  return blocks;
}

export function MarkdownLite({ content }: { content: string }) {
  const blocks = parseBlocks(content);
  return (
    <Stack gap="large">
      {blocks.map((block, blockIndex) =>
        block.kind === 'paragraph' ? (
          <Text key={blockIndex} size="small">
            {renderInline(block.text)}
          </Text>
        ) : (
          <Stack key={blockIndex} gap="medium">
            {block.items.map((item, itemIndex) => (
              <div key={itemIndex} style={{ display: 'flex', gap: 8 }}>
                <span style={{ flexShrink: 0 }}>
                  <Text size="small" tone="muted">
                    {item.marker}
                  </Text>
                </span>
                <Text size="small">{renderInline(item.text)}</Text>
              </div>
            ))}
          </Stack>
        ),
      )}
    </Stack>
  );
}
